import { useCallback, useEffect, useState } from "react";
import { BellRing, Loader2, MessageSquareWarning, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, Kpi, Loading, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const STATUS_STYLE = {
  information: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  review: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  inquiry: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  disciplinary: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  closed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  parked: "border-border bg-muted text-muted-foreground",
  rejected: "border-border bg-muted text-muted-foreground",
};

export const Inquiries = () => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState({});
  const [replyOpen, setReplyOpen] = useState("");
  const [editOpen, setEditOpen] = useState("");
  const [edit, setEdit] = useState({ question: "", query_type: "" });
  const [steer, setSteer] = useState({ status: "", remark: "" });
  const [busy, setBusy] = useState("");
  const detail = useRowDetail();

  const load = useCallback(async () => {
    const res = await api.get("/inquiries");
    setData(res.data);
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const respond = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/inquiries/${row.id}/respond`, { response: reply[row.id] || "" });
      toast.success("Your explanation has been sent");
      setReply({ ...reply, [row.id]: "" });
      setReplyOpen("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const amend = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/inquiries/${row.id}/edit`, edit);
      toast.success(`${row.inquiry_ref} amended`);
      setEditOpen("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const move = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/inquiries/${row.id}/status`, steer);
      toast.success(`${row.inquiry_ref} updated`);
      setSteer({ status: "", remark: "" });
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const chase = async () => {
    setBusy("chase");
    try {
      const res = await api.post("/inquiries/reminders/run", {});
      const { reminded = [], escalated = [], skipped = 0 } = res.data;
      toast.success(reminded.length + escalated.length
        ? `${reminded.length + escalated.length} reminder(s) sent`
          + (escalated.length ? `, ${escalated.length} escalated to the Board Secretary` : "")
        : `Nothing to chase — ${skipped} query(ies) were already reminded today`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading queries" />;
  const label = (key) => (data.statuses.find((s) => s.key === key) || {}).label || key;

  return (
    <div className="space-y-4" data-testid="inquiries-panel">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-inquiries-open" label="Open queries" value={data.counters.open}
             accent="amber" icon={MessageSquareWarning} />
        <Kpi testId="kpi-inquiries-you" label="Awaiting your reply" value={data.awaiting_you}
             accent="sky" />
        <Kpi testId="kpi-inquiries-disciplinary" label="Disciplinary" accent="rose"
             value={data.counters.disciplinary} />
        <Kpi testId="kpi-inquiries-closed" label="Closed" value={data.counters.closed}
             accent="emerald" />
      </div>

      <Panel testId="panel-inquiries" kicker="Audit queries"
             title="Explanations asked of the people behind the entries"
             actions={
               <Button size="sm" variant="outline" onClick={chase} disabled={busy === "chase"}
                       data-testid="inquiry-remind-button"
                       className="h-8 rounded-full border-border text-xs">
                 {busy === "chase" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <BellRing className="mr-1 h-3.5 w-3.5" />} Send reminders now
               </Button>
             }>
        <Table testId="inquiries-table"
               head={<><Th>Reference</Th><Th>About</Th><Th>Asked of</Th><Th>Status</Th>
                 <Th>Remark</Th><Th>Replies</Th><Th>Raised</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={8} label="No queries raised yet" />}
          {data.items.map((q) => (
            <tr key={q.id} className="row-hover cursor-pointer"
                data-testid={`inquiry-row-${q.inquiry_ref}`}
                onClick={rowClick(() => detail.show(q, q.inquiry_ref, q.inquiry_ref,
                                                    q.audit_action))}>
              <Td className="font-mono text-[11px]">{q.inquiry_ref}</Td>
              <Td>
                <div className="text-foreground">{q.audit_action}</div>
                <div className="font-mono text-[10.5px] text-muted-foreground">
                  #{q.audit_seq} · {q.audit_resource} {q.audit_resource_id || ""}
                </div>
              </Td>
              <Td className="text-[11.5px]">{q.respondent}</Td>
              <Td><Chip className={STATUS_STYLE[q.status]}>{label(q.status)}</Chip></Td>
              <Td className="max-w-[240px] text-[11px] text-muted-foreground">{q.remark}</Td>
              <Td>{q.responses?.length || 0}</Td>
              <Td className="font-mono text-[10.5px]">{dateTime(q.created_at)}</Td>
              <Td>
                <div className="flex justify-end gap-1.5">
                  {q.respondent === data.you && !["closed", "rejected"].includes(q.status) && (
                    <Dialog open={replyOpen === q.id}
                            onOpenChange={(o) => setReplyOpen(o ? q.id : "")}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline"
                                data-testid={`inquiry-respond-${q.inquiry_ref}`}
                                className="h-7 rounded-full border-border px-2.5 text-[10px]">
                          Explain
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle>Answer {q.inquiry_ref}</DialogTitle></DialogHeader>
                        <p className="text-[12px] text-muted-foreground">{q.question}</p>
                        <Textarea rows={5} value={reply[q.id] || ""}
                                  data-testid="inquiry-response-input"
                                  onChange={(e) => setReply({ ...reply, [q.id]: e.target.value })}
                                  placeholder="Explain what you did and why"
                                  className="bg-muted/40 text-[12.5px]" />
                        <DialogFooter>
                          <Button onClick={() => respond(q)} disabled={busy === q.id}
                                  data-testid="inquiry-response-send"
                                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                            {busy === q.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              : <Send className="mr-2 h-4 w-4" />} Send explanation
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {(q.raised_by === data.you || data.may_steer)
                    && !["closed", "rejected", "parked"].includes(q.status) && (
                    <Dialog open={editOpen === q.id}
                            onOpenChange={(o) => {
                              setEditOpen(o ? q.id : "");
                              if (o) setEdit({ question: q.question, query_type: q.query_type });
                            }}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline"
                                data-testid={`inquiry-edit-${q.inquiry_ref}`}
                                className="h-7 rounded-full border-border px-2.5 text-[10px]">
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Amend {q.inquiry_ref}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">Query type</Label>
                            <Select value={edit.query_type}
                                    onValueChange={(v) => setEdit({ ...edit, query_type: v })}>
                              <SelectTrigger data-testid="inquiry-edit-type" className="bg-muted/40">
                                <SelectValue /></SelectTrigger>
                              <SelectContent>
                                {data.types.map((t) => (
                                  <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">Question</Label>
                            <Textarea rows={5} value={edit.question}
                                      data-testid="inquiry-edit-question"
                                      onChange={(e) => setEdit({ ...edit, question: e.target.value })}
                                      className="bg-muted/40 text-[12.5px]" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={() => amend(q)} disabled={busy === q.id}
                                  data-testid="inquiry-edit-save"
                                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                            {busy === q.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save changes
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {data.may_steer && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline"
                                data-testid={`inquiry-steer-${q.inquiry_ref}`}
                                className="h-7 rounded-full border-border px-2.5 text-[10px]">
                          Set status
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>Move {q.inquiry_ref} on</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">Status</Label>
                            <Select value={steer.status}
                                    onValueChange={(v) => setSteer({ ...steer, status: v })}>
                              <SelectTrigger data-testid="inquiry-status-select" className="bg-muted/40">
                                <SelectValue placeholder="Choose a status" /></SelectTrigger>
                              <SelectContent>
                                {data.statuses.map((s) => (
                                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground">
                              Remark (shown on the register)
                            </Label>
                            <Textarea rows={3} value={steer.remark}
                                      data-testid="inquiry-remark-input"
                                      onChange={(e) => setSteer({ ...steer, remark: e.target.value })}
                                      className="bg-muted/40 text-[12.5px]" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={() => move(q)} disabled={busy === q.id || !steer.status}
                                  data-testid="inquiry-status-save"
                                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                            {busy === q.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Update query
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
      {detail.sheet}
    </div>
  );
};
